import { token } from '@atlaskit/tokens';
import { collabStateManager } from '@databases/collab-state-manager';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import {
  isEmbed,
  isAnyNativeEmbed,
  isNativeEmbed,
  isNativeEmbedEditorPlugin,
} from '@databases/modes';
import usePanelState from '@databases/panel/usePanelState';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { selectIsSoftSelectEnabled } from '@databases/redux/cellSelections/selectors';
import { getService } from '@databases/service-container';
import { type DatabaseLayoutType } from '@databases/types/database/view';
import { type DatabaseBridgeAPI, getBridgeAPI } from '@databases/utils/database/bridge';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import MainPanel from './MainPanel';
import PersistedPanelGroup from './PersistedPanelGroup';
import SidePanel from './SidePanel';
import { createPanelStorageKey } from './panel/utils';

type LayoutProps = {
  banners?: React.ReactNode;
  options?: {
    toolbarLocation: 'top' | 'views';
  };
  view: React.ReactNode;
  sidebar?: React.ReactNode | null;
  toolbar: React.ReactNode;
  outerContainerRef?: HTMLElement | null;
  isReadOnly?: boolean;
  layoutType?: DatabaseLayoutType;
};

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  position: absolute;
  width: 100%;
  overflow: hidden;
  left: 0;
  top: 0;
  margin-right: ${token('space.100')};

  #app-resize-handle,
  #app-overlay-resize-handle {
    border-left-width: ${token('border.width.selected', '2px')};
    border-left-style: solid;
    border-left-color: ${token('color.border', '#091E4224')};
  }

  #app-resize-handle[data-resize-handle-state='hover'],
  #app-overlay-resize-handle[data-resize-handle-state='hover'] {
    border-left-color: ${token('color.border.focused', '#388BFF')};
  }

  #app-resize-handle[data-resize-handle-state='drag'],
  #app-overlay-resize-handle[data-resize-handle-state='drag'] {
    border-left-color: ${token('color.border.selected', '#0C66E4')};
  }

  #right-side-panel {
    height: 100%;
  }
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  min-height: 0;
  flex-shrink: 1;
  display: flex;
  flex-direction: row;
`;

const DatabaseLayout = ({
  banners,
  view,
  sidebar,
  toolbar,
  options = {
    toolbarLocation: 'top',
  },
  outerContainerRef,
  isReadOnly,
  layoutType,
}: LayoutProps) => {
  const [bridgeApi, setBridgeApi] = useState<DatabaseBridgeAPI | null>(null);
  const versionId = getVersionId();
  const [isRightGutterCollapsed, setIsRightGutterCollapsed] = useState<boolean>(false);
  const isEmbedView = isEmbed();
  const { getPanelKey, isPanelVisible } = usePanelState();
  const isNativeEmbedView = isAnyNativeEmbed();
  const { contentId } = useDatabaseContent();

  useEffect(() => {
    const loadBridgeApi = async () => {
      await getBridgeAPI().then(setBridgeApi);
    };
    loadBridgeApi();
  }, []);

  useEffect(() => {
    if (bridgeApi) {
      bridgeApi.objectSidebarMethods?.registerChangeObjectSidebarShownHandler(
        (isShown?: boolean) => {
          setIsRightGutterCollapsed(!!isShown);
        }
      );
    }
  }, [bridgeApi]);

  useEffect(() => {
    const isViewingVersion = !!versionId;
    // collapse gutter when viewing a version
    setIsRightGutterCollapsed(isViewingVersion);
  }, [versionId]);

  const isOverlayMode = isNativeEmbedEditorPlugin();
  const panelKey = getPanelKey();
  // In overlay mode, the panel group has only 1 panel (main). Persisted layouts that were saved
  // when the sidebar was part of the group (2 panels) will crash `react-resizable-panels` with
  // "Invalid 1 panel layout". Use a stable no-panel key so we never restore an incompatible layout.
  const storageKey = useMemo(
    () => createPanelStorageKey(isOverlayMode ? null : panelKey),
    [isOverlayMode, panelKey]
  );

  const selectionsActions = getService('SelectionsActions');

  const isSoftSelected = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    return selectIsSoftSelectEnabled(state);
  });

  const collabManager = collabStateManager.getCollabManager(contentId ?? '');

  const isDatabasePresenceEnabled = getExperimentValue('databasePresence', 'enabled', {
    fireExperimentExposure: false,
  });

  /**
   * There is no need for an outside click handler as our mouse input
   * in the selections engine will handle this.
   */
  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  const onLayoutContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const target = e.target as HTMLElement;

      if (
        'ariaLabel' in target &&
        target.ariaLabel !== 'dragHandlerIcon' &&
        target.hasAttribute('data-testid') &&
        target.getAttribute('data-testid') !== 'database-value-view-container'
      ) {
        if (!isSoftSelected) {
          // TODO: Remove once performance work is shipped
          if (collabManager && isDatabasePresenceEnabled) {
            collabManager.updateTelepointer(undefined, undefined);
          }

          selectionsActions.deselectAll();
        }
      }
    },
    [collabManager, isDatabasePresenceEnabled, isSoftSelected, selectionsActions]
  );

  return (
    <LayoutContainer
      id="layout-container"
      data-testid="layout-container"
      onClick={isSelectionsPerformanceEnabled ? undefined : onLayoutContainerClick}
    >
      {banners && <div data-testid="layout-banner-wrapper">{banners}</div>}

      <ContentWrapper data-testid="layout-content-wrapper">
        <PersistedPanelGroup
          storageKey={storageKey}
          outerContainerRef={outerContainerRef}
        >
          <MainPanel
            isEmbedView={isEmbedView}
            isNativeEmbedView={isNativeEmbedView}
            isRightGutterCollapsed={isRightGutterCollapsed}
            isSidePanelVisible={isPanelVisible()}
            options={options}
            view={view}
            toolbar={toolbar}
            isReadOnly={isReadOnly}
            layoutType={layoutType}
          />

          {!isOverlayMode && (
            <SidePanel
              sidebar={sidebar}
              resizeDisabled={isNativeEmbed()}
              contentId={contentId}
            />
          )}
        </PersistedPanelGroup>
      </ContentWrapper>

      {isOverlayMode && (
        <SidePanel
          sidebar={sidebar}
          overlayMode
          contentId={contentId}
          isSidePanelVisible={isPanelVisible()}
        />
      )}
    </LayoutContainer>
  );
};

export default DatabaseLayout;
